import { useEffect, useRef, useState } from "react";
import { getAudio } from "@/audio";

export const MEMORY_SECONDS = 23;

const pickType = () =>
  ["video/webm;codecs=vp9,opus", "video/webm;codecs=vp8,opus", "video/webm", "video/mp4"].find((t) => MediaRecorder.isTypeSupported?.(t)) || "";

const download = (blob, type) => {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `eigengrau-memory-${new Date().toISOString().replace(/[:.]/g, "-")}.${type.includes("mp4") ? "mp4" : "webm"}`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 10000);
};

export const useMemory = (canvasRef, fishRef) => {
  const [left, setLeft] = useState(0);
  const recRef = useRef(null);
  const timerRef = useRef(null);
  const supported = typeof window !== "undefined" && !!window.MediaRecorder && !!HTMLCanvasElement.prototype.captureStream;

  useEffect(() => () => {
    clearInterval(timerRef.current);
    if (recRef.current?.state === "recording") {
      recRef.current.onstop = null;
      recRef.current.stop();
    }
  }, []);

  const record = () => {
    if (!supported || left) return;
    const src = canvasRef.current;
    const comp = Object.assign(document.createElement("canvas"), { width: src.width, height: src.height });
    const cctx = comp.getContext("2d");
    let raf;
    const paint = () => {
      cctx.drawImage(src, 0, 0);
      if (fishRef?.current) cctx.drawImage(fishRef.current, 0, 0);
      raf = requestAnimationFrame(paint);
    };
    paint();
    const video = comp.captureStream(30);
    const { rec } = getAudio();
    const stream = new MediaStream([...video.getVideoTracks(), ...(rec ? rec.stream.getAudioTracks() : [])]);
    const type = pickType();
    let recorder;
    try {
      recorder = new MediaRecorder(stream, type ? { mimeType: type } : undefined);
    } catch {
      cancelAnimationFrame(raf);
      video.getTracks().forEach((t) => t.stop());
      return;
    }
    const chunks = [];
    recorder.ondataavailable = (e) => e.data.size && chunks.push(e.data);
    recorder.onstop = () => {
      cancelAnimationFrame(raf);
      video.getTracks().forEach((t) => t.stop());
      setLeft(0);
      download(new Blob(chunks, { type: recorder.mimeType || type || "video/webm" }), recorder.mimeType || type);
    };
    recRef.current = recorder;
    recorder.start(1000);
    setLeft(MEMORY_SECONDS);
    const end = Date.now() + MEMORY_SECONDS * 1000;
    const id = setInterval(() => {
      const s = Math.max(0, Math.ceil((end - Date.now()) / 1000));
      if (s > 0) return setLeft(s);
      clearInterval(id);
      if (recorder.state === "recording") recorder.stop();
    }, 250);
    timerRef.current = id;
  };

  return { record, left, recording: left > 0, supported };
};
